/**
 * Visualization grants for charts drawn on a public newsroom article.
 *
 * A grant is a token that reads ONE visualization with ONE frozen view (filters, start,
 * end, grain): the backend replaces whatever query a chart sends with the grant's pinned
 * one. The article chart provides its grant to its subtree; every chart component that
 * fetches a visualization reads it here and sends it along, on the anonymous client.
 *
 * Provided per chart, not looked up by visualization id: one article can show the same
 * visualization twice with different filters, and each needs its own grant.
 */
import { computed, getCurrentInstance, inject, provide, unref } from 'vue';

export const VISUALIZATION_GRANT_KEY = Symbol('visualization-grant');

/** @param {import('vue').Ref<string|null>|string|null} grant */
export function provideVisualizationGrant(grant) {
  provide(VISUALIZATION_GRANT_KEY, grant);
}

/** The grant this chart is drawn under, or null (any chart outside a newsroom article). */
export function useVisualizationGrant() {
  // Outside a component (unit tests of composables) there is nothing to inject.
  const provided = getCurrentInstance() ? inject(VISUALIZATION_GRANT_KEY, null) : null;
  return computed(() => unref(provided) || null);
}

/**
 * `getVisualization` that sends the injected grant, if any. Call in setup.
 * Lazy import keeps Client → i18n out of module evaluation, as `useChartData` does.
 */
export function useGrantedGetVisualization() {
  const grant = useVisualizationGrant();
  return async (datasetId, visualizationId, params = {}) => {
    const { getVisualization } = await import('@/utils/datasetConfig');
    return getVisualization(
      datasetId,
      visualizationId,
      grant.value ? { ...params, grant: grant.value } : params
    );
  };
}
